import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  ShoppingCart, Plus, Check, X, Trash2, CheckCircle2, Circle, Clock,
} from "lucide-react";
import { useAuth } from "../lib/auth";

const fmt = (iso) => iso ? new Date(iso).toLocaleString("nb-NO", {
  day: "2-digit", month: "2-digit", year: "2-digit",
  hour: "2-digit", minute: "2-digit",
}) : "—";

function AddDialog({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", quantity: 1, estimated_price_eur: "", supplier: "",
  });
  const submit = async () => {
    if (!form.title.trim()) return toast.error("Tittel mangler");
    try {
      await api.post("/purchases", {
        title: form.title.trim(),
        description: form.description.trim(),
        quantity: parseInt(form.quantity, 10) || 1,
        estimated_price_eur: form.estimated_price_eur === ""
          ? null : parseFloat(form.estimated_price_eur),
        supplier: form.supplier.trim() || null,
      });
      toast.success("Lagt til");
      setOpen(false);
      setForm({ title: "", description: "", quantity: 1, estimated_price_eur: "", supplier: "" });
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Kunne ikke lagre");
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="purchase-add-open" className="bg-[#BD984D] hover:bg-[#A57F39] text-white">
          <Plus className="w-4 h-4 mr-1" /> Legg til
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Ny innkjøpsoppføring</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Tittel</Label>
            <Input value={form.title} data-testid="purchase-title"
                   onChange={(e) => setForm({ ...form, title: e.target.value })}
                   placeholder="F.eks. Vindusvisker 16'' Bosch" />
          </div>
          <div>
            <Label>Beskrivelse / hvorfor</Label>
            <Textarea rows={3} value={form.description}
                      data-testid="purchase-description"
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      placeholder="Hvilken båt, hvilket avvik, leverandør osv." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Antall</Label>
              <Input type="number" min={1} value={form.quantity}
                     data-testid="purchase-quantity"
                     onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
            </div>
            <div>
              <Label>Estimert pris (€)</Label>
              <Input type="number" step="0.01" min={0} value={form.estimated_price_eur}
                     data-testid="purchase-price"
                     onChange={(e) => setForm({ ...form, estimated_price_eur: e.target.value })}
                     placeholder="—" />
            </div>
          </div>
          <div>
            <Label>Leverandør (valgfri)</Label>
            <Input value={form.supplier}
                   data-testid="purchase-supplier"
                   onChange={(e) => setForm({ ...form, supplier: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} data-testid="purchase-add-save"
                  className="bg-[#1DA3B3] hover:bg-[#2FB5C5] text-white">
            Lagre
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ p, isAdmin, reload }) {
  const setDecision = async (decision) => {
    try {
      await api.post(`/purchases/${p.id}/decision`, { decision });
      toast.success(decision === "approved" ? "Godkjent"
        : decision === "rejected" ? "Avvist" : "Tilbakestilt");
      reload();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Feilet");
    }
  };
  const setPurchased = async (purchased) => {
    try {
      await api.post(`/purchases/${p.id}/purchased`, { purchased });
      toast.success(purchased ? "Markert som kjøpt" : "Markert som ikke kjøpt");
      reload();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Feilet");
    }
  };
  const remove = async () => {
    if (!window.confirm("Slette denne oppføringen?")) return;
    try {
      await api.delete(`/purchases/${p.id}`);
      toast.success("Slettet");
      reload();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Feilet");
    }
  };

  const approvalBadge = p.approval_status === "approved"
    ? <Badge className="bg-emerald-100 text-emerald-700 border-0"><CheckCircle2 className="w-3 h-3 mr-1" /> Godkjent</Badge>
    : p.approval_status === "rejected"
      ? <Badge className="bg-red-100 text-red-700 border-0"><X className="w-3 h-3 mr-1" /> Ikke godkjent</Badge>
      : <Badge className="bg-[#BD984D]/15 text-[#A57F39] border-0"><Clock className="w-3 h-3 mr-1" /> Avventer</Badge>;

  const purchaseBadge = p.purchase_status === "purchased"
    ? <Badge className="bg-[#1DA3B3]/15 text-[#1DA3B3] border-0"><CheckCircle2 className="w-3 h-3 mr-1" /> Kjøpt</Badge>
    : <Badge className="bg-slate-100 text-slate-600 border-0"><Circle className="w-3 h-3 mr-1" /> Ikke kjøpt</Badge>;

  return (
    <Card className="p-4" data-testid={`purchase-row-${p.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-base font-semibold text-[#0C1824]">{p.title}</span>
            {p.quantity > 1 && (
              <Badge className="bg-slate-100 text-slate-700 border-0">×{p.quantity}</Badge>
            )}
            {approvalBadge}
            {purchaseBadge}
          </div>
          {p.description && (
            <p className="text-sm text-[#2B3F4D] mt-1">{p.description}</p>
          )}
          <div className="text-xs text-[#2B3F4D] mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
            {p.estimated_price_eur != null && (
              <span>Estimert: <strong className="font-mono">{p.estimated_price_eur} €</strong></span>
            )}
            {p.actual_price_eur != null && (
              <span>Betalt: <strong className="font-mono text-[#1DA3B3]">{p.actual_price_eur} €</strong></span>
            )}
            {p.supplier && <span>Leverandør: {p.supplier}</span>}
            <span>Foreslått av {p.created_by_name} · {fmt(p.created_at)}</span>
            {p.approved_by_name && (
              <span>· {p.approval_status === "approved" ? "Godkjent" : "Avvist"} av {p.approved_by_name} {fmt(p.approved_at)}</span>
            )}
            {p.purchased_by_name && p.purchase_status === "purchased" && (
              <span>· Kjøpt av {p.purchased_by_name} {fmt(p.purchased_at)}</span>
            )}
          </div>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap gap-1.5 items-center">
            {p.approval_status !== "approved" && (
              <Button size="sm" onClick={() => setDecision("approved")}
                      data-testid={`purchase-approve-${p.id}`}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Check className="w-3.5 h-3.5 mr-1" /> Godkjenn
              </Button>
            )}
            {p.approval_status !== "rejected" && (
              <Button size="sm" variant="outline" onClick={() => setDecision("rejected")}
                      data-testid={`purchase-reject-${p.id}`}
                      className="border-red-300 text-red-600 hover:bg-red-50">
                <X className="w-3.5 h-3.5 mr-1" /> Ikke godkjenn
              </Button>
            )}
            {p.purchase_status === "purchased" ? (
              <Button size="sm" variant="outline" onClick={() => setPurchased(false)}
                      data-testid={`purchase-unmark-${p.id}`}
                      className="border-slate-300 text-slate-700">
                <Circle className="w-3.5 h-3.5 mr-1" /> Ikke kjøpt
              </Button>
            ) : (
              <Button size="sm" onClick={() => setPurchased(true)}
                      data-testid={`purchase-mark-${p.id}`}
                      className="bg-[#1DA3B3] hover:bg-[#2FB5C5] text-white">
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Kjøpt
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={remove}
                    data-testid={`purchase-delete-${p.id}`}
                    className="border-red-300 text-red-600 hover:bg-red-50">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

export default function PurchasesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("active");

  const reload = async () => {
    try {
      const r = await api.get("/purchases");
      setItems(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Kunne ikke hente");
    }
  };
  useEffect(() => { reload(); }, []);

  const filtered = useMemo(() => {
    if (tab === "pending") return items.filter((p) => p.approval_status === "pending");
    if (tab === "approved") return items.filter((p) =>
      p.approval_status === "approved" && p.purchase_status !== "purchased");
    if (tab === "purchased") return items.filter((p) => p.purchase_status === "purchased");
    if (tab === "rejected") return items.filter((p) => p.approval_status === "rejected");
    // active = ikke avvist OG ikke kjøpt
    return items.filter((p) =>
      p.approval_status !== "rejected" && p.purchase_status !== "purchased");
  }, [items, tab]);

  const counts = useMemo(() => ({
    active: items.filter((p) => p.approval_status !== "rejected" && p.purchase_status !== "purchased").length,
    pending: items.filter((p) => p.approval_status === "pending").length,
    approved: items.filter((p) => p.approval_status === "approved" && p.purchase_status !== "purchased").length,
    purchased: items.filter((p) => p.purchase_status === "purchased").length,
    rejected: items.filter((p) => p.approval_status === "rejected").length,
  }), [items]);

  return (
    <div className="space-y-5" data-testid="purchases-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.32em] text-[#A57F39] flex items-center gap-2">
            <ShoppingCart className="w-3.5 h-3.5" /> Innkjøp
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0C1824] mt-1">Innkjøpsliste</h1>
          <p className="text-sm text-[#2B3F4D] mt-1 max-w-2xl">
            Hold oversikt over deler, forbruksvarer og annet som skal kjøpes inn –
            f.eks. for å dekke avvik. Godkjenn forslag og merk dem som kjøpt når de er ordnet.
          </p>
        </div>
        <AddDialog onSaved={reload} />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="overflow-x-auto -mx-1 pb-1">
          <TabsList className="w-max min-w-full">
            <TabsTrigger value="active" data-testid="purchases-tab-active">
              Aktive ({counts.active})
            </TabsTrigger>
            <TabsTrigger value="pending" data-testid="purchases-tab-pending">
              Avventer godkjenning ({counts.pending})
            </TabsTrigger>
            <TabsTrigger value="approved" data-testid="purchases-tab-approved">
              Godkjente ({counts.approved})
            </TabsTrigger>
            <TabsTrigger value="purchased" data-testid="purchases-tab-purchased">
              Kjøpt ({counts.purchased})
            </TabsTrigger>
            <TabsTrigger value="rejected" data-testid="purchases-tab-rejected">
              Avvist ({counts.rejected})
            </TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value={tab} className="mt-4">
          {filtered.length === 0 ? (
            <Card className="p-6 text-sm text-[#2B3F4D]">Ingen oppføringer i denne kategorien.</Card>
          ) : (
            <div className="space-y-2">
              {filtered.map((p) => (
                <Row key={p.id} p={p} isAdmin={isAdmin} reload={reload} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
